// "Brutta giornata" snooze for the downward MAX-regression prompt
// (next/coaching.md §9). When the athlete taps "Bad day" on a ⬇️ prompt
// they're saying "probably just an off day, don't nag me" — so we silence
// the prompt for that exercise and only re-surface it if the regression
// PERSISTS across further sessions (a real decline, not a one-off).
//
// Pure logic only — storage I/O lives in each platform's useMaxBumpFlow
// (mobile: AsyncStorage; web-rnw: lsCache). Lives in @jackedlogs/shared,
// alongside maxBumpCandidates.ts, so both hosts read the SAME "when does it
// re-surface" rule instead of forking it.

// How many further qualifying sessions a snoozed exercise must show the
// regression before the prompt re-surfaces. 2 = forgive one bad day, then
// resurface once it recurs across two more sessions. Tunable like δ/N
// (Antenore can revise after living with it).
export const SNOOZE_RESURFACE_RECURRENCES = 2;

// exerciseId → number of qualifying sessions observed SINCE the snooze was
// set (0 right after "Bad day"). Absence = not snoozed.
export type RegressionSnoozeState = Record<string, number>;

export const isSnoozeState = (v: unknown): v is RegressionSnoozeState => {
  if (typeof v !== "object" || v === null || Array.isArray(v)) return false;
  return Object.values(v).every((n) => typeof n === "number");
};

// Set a fresh snooze for an exercise (after "Bad day").
export const snoozeExercise = (
  state: RegressionSnoozeState,
  exerciseId: string,
): RegressionSnoozeState => ({ ...state, [exerciseId]: 0 });

// Clear a snooze (the prompt was resolved — Update or Keep, or the
// regression no longer qualifies).
export const clearSnooze = (
  state: RegressionSnoozeState,
  exerciseId: string,
): RegressionSnoozeState => {
  if (!(exerciseId in state)) return state;
  const next = { ...state };
  delete next[exerciseId];
  return next;
};

// Given the exercises that produced a LOWER (regression) candidate this
// session and the current snooze state, decide which to SUPPRESS and
// return the updated state. A snoozed exercise that recurs increments its
// counter; once it reaches SNOOZE_RESURFACE_RECURRENCES the prompt
// re-surfaces (kept) and the snooze clears. Non-snoozed exercises always
// show.
export const partitionSnoozedRegressions = (
  lowerExerciseIds: readonly string[],
  state: RegressionSnoozeState,
): { suppress: ReadonlySet<string>; next: RegressionSnoozeState } => {
  const suppress = new Set<string>();
  // Clone lazily: when no snoozed exercise is involved, return the SAME
  // reference so the caller can skip a redundant persist.
  let next: RegressionSnoozeState | null = null;
  for (const id of lowerExerciseIds) {
    if (!(id in state)) continue; // not snoozed → always show
    if (!next) next = { ...state };
    const recurrences = state[id]! + 1; // guarded by the `in` check above
    if (recurrences >= SNOOZE_RESURFACE_RECURRENCES) {
      delete next[id]; // persisted decline → re-surface + clear snooze
    } else {
      next[id] = recurrences; // still within the grace window → suppress
      suppress.add(id);
    }
  }
  return { suppress, next: next ?? state };
};
